import { services } from '@config/services';
import type {
  InstantiateFromTemplatePayload,
  InstantiateFromTemplateResponse,
  Template,
  TemplatesListResponse,
} from '../types';
import { integrationApi } from './_base';

/**
 * Plugin-template endpoints (`/v1/template*`) plus the one plugin route that
 * belongs to the template flow (`POST /v1/plugin/fromTemplate`). The rest of
 * the plugin-record CRUD lives in `./plugins.ts`.
 *
 * Every endpoint here is dark until `om_tech_21417_plugin_templates` is on —
 * integration-service returns 404 while the flag is off, so callers must be
 * behind the flag gate (see `../Templates/flag.ts`) rather than relying on an
 * empty list.
 */

export interface TemplateListArgs {
  /** Lifecycle filter. The marketplace passes `Active` — deprecated and
   * retired templates are not instantiable and must not be offered. */
  status?: string;
  templateType?: string;
}

/**
 * Request descriptors exported separately so the wire-shape lock tests can
 * assert URL/method/body without going through the network layer (mirrors
 * `phase5RequestBuilders` in ./plugins.ts).
 */
export const templateRequestBuilders = {
  getTemplates: (args: TemplateListArgs | void): { url: string; method: 'GET' } => {
    const p = new URLSearchParams();
    if (args?.status) p.set('status', args.status);
    if (args?.templateType) p.set('templateType', args.templateType);
    const qs = p.toString();
    return {
      url: `${services.integration}/v1/template${qs ? `?${qs}` : ''}`,
      method: 'GET',
    };
  },
  getTemplate: (templateId: string): { url: string; method: 'GET' } => ({
    url: `${services.integration}/v1/template/${encodeURIComponent(templateId)}`,
    method: 'GET',
  }),
  instantiateFromTemplate: (
    body: InstantiateFromTemplatePayload,
  ): { url: string; method: 'POST'; body: InstantiateFromTemplatePayload } => ({
    url: `${services.integration}/v1/plugin/fromTemplate`,
    method: 'POST',
    body,
  }),
};

const templatesApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * `GET /v1/template?status=Active` → `{ templates, total }`. Deliberately
     * unpaginated server-side — the catalog is a handful of admin-curated
     * rows and the Templates tab renders all of them at once.
     */
    getTemplates: builder.query<TemplatesListResponse, TemplateListArgs | void>({
      query: (args) => templateRequestBuilders.getTemplates(args),
      transformResponse: (
        response: TemplatesListResponse | Template[],
      ): TemplatesListResponse => {
        if (Array.isArray(response)) return { templates: response, total: response.length };
        return {
          templates: response?.templates ?? [],
          total: response?.total ?? response?.templates?.length ?? 0,
        };
      },
      providesTags: (result) =>
        result?.templates
          ? [
              ...result.templates.map((tpl) => ({ type: 'Templates' as const, id: tpl.id })),
              { type: 'Templates' as const, id: 'LIST' },
            ]
          : [{ type: 'Templates' as const, id: 'LIST' }],
    }),
    /**
     * `GET /v1/template/{templateId}` — the single template the wizard
     * configures against. Fetched on its own rather than handed through
     * router state so a deep link / refresh of the wizard URL still works.
     */
    getTemplate: builder.query<Template, string>({
      query: (templateId) => templateRequestBuilders.getTemplate(templateId),
      transformResponse: (response: Template | { template?: Template }): Template => {
        const wrapped = response as { template?: Template };
        return wrapped.template ?? (response as Template);
      },
      providesTags: (_r, _e, templateId) => [{ type: 'Templates', id: templateId }],
    }),
    /**
     * `POST /v1/plugin/fromTemplate` — create a plugin from a template.
     * Returns the plugin plus the id of its first version, which the service
     * creates as a DRAFT; publishing it is a later-epic action.
     *
     * Invalidates the plugin LIST so the new instance shows up in Manage
     * Plugins without a manual refresh. Templates themselves are unchanged.
     */
    instantiateFromTemplate: builder.mutation<
      InstantiateFromTemplateResponse,
      InstantiateFromTemplatePayload
    >({
      query: templateRequestBuilders.instantiateFromTemplate,
      invalidatesTags: [{ type: 'Plugins', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useInstantiateFromTemplateMutation,
} = templatesApi;
